/**
 * 送り始めの 1 日の上限（warmup）の帯。書く画面の「送るとき」のすぐ上に出す（2026-09-27 creator/mail-warmup）。
 *
 * WHY 送る操作の近くに置くか: 上限を超えた分は断られずに翌日へ回る（Core の方針）。送った後で「半分しか届いていない」と驚かないよう、
 *   押す前の視線の先で「今日あと何通・超えた分はいつ」を見せる。上限の期間を過ぎたら（warmup = null）何も出さない。
 * 時刻は現地時刻（fullDate = ブラウザのタイムゾーン）で、「（あなたの時刻）」を添える（書く画面の予約と同じ書き方）。
 * 数字の意味（なぜこの上限か）は説明しない（mailWarmup.ts の冒頭・Core の方針）。
 *
 * 2026-09-27 creator/mail-identity（Core の mail-prelaunch）: 払い出しの本人確認が済んでいない間は、8 日目以降も上限が続く。
 *   そのときは「送り始めの 7 日間」「○日目 / 7 日」「上限は○までです」を出さず（day は 8 以上・endsAt は過去になる = 崩れる / 誤る）、
 *   「1 日に送れる数の上限」として書き、Money の案の文言と受け取りの登録（お金の画面）へのボタンを添える（limitKind・needsIdentity の WHY）。
 *   リンク先は本番の口座（/money/live/get-paid）: Core の判定（Money の payoutIdentityVerified）は本番の口座だけを見る = テストの口座で済ませても上限は変わらない
 */
import { Link } from 'react-router-dom';
import { fullDate } from '../../components/ui';
import { useT } from '../../i18n';
import { limitKind, needsIdentity, showEndsAt, type MailWarmup } from '../../mailWarmup';
import { isMoneyNavHidden } from '../../launchHidden';

/** 受け取りの登録（お金の画面・App.tsx の /money/:mode/get-paid）。上の WHY の通り本番の口座 */
export const PAYOUT_SETUP_PATH = '/money/live/get-paid';

export function WarmupBand({ warmup }: { warmup: MailWarmup | null }) {
  const { t } = useT();
  if (!warmup) return null;
  // まだ一度も送っていない = 「日」が始まっていない（startedAt が null）。今日の残りではなく 1 日目の上限として書く
  const started = !!warmup.startedAt;
  const identity = limitKind(warmup) === 'identity';
  return (
    <div className="alert alert-info mail-warmup" role="note">
      <strong>
        {t(identity ? 'mail.warmup.identityTitle' : 'mail.warmup.title')}
        {started && !identity && <span className="small muted"> · {t('mail.warmup.day', { day: warmup.day, days: warmup.days })}</span>}
      </strong>
      <span>
        {identity
          ? started
            ? t('mail.warmup.identityBody', { limit: warmup.dailyLimit, remaining: warmup.remainingToday })
            : t('mail.warmup.identityNotStarted', { limit: warmup.dailyLimit })
          : started
            ? t('mail.warmup.body', { days: warmup.days, limit: warmup.dailyLimit, remaining: warmup.remainingToday })
            : t('mail.warmup.notStarted', { days: warmup.days, limit: warmup.dailyLimit })}
      </span>
      {warmup.pausedUntil && <span>{t('mail.warmup.paused', { at: fullDate(warmup.pausedUntil) })}</span>}
      {showEndsAt(warmup) && <span className="small">{t('mail.warmup.ends', { at: fullDate(warmup.endsAt!) })}</span>}
      {/* 受け取りの登録（payouts）を見せない間は、そこへの案内とボタンを出さない（2026-09-29 creator/launch-money・launchHidden.ts）。
          押しても前払い残高へ戻されるだけ = 行き止まりの案内になる。上限の文（identityBody = 「1 日に送れる数に上限があります」）はそのまま出す。
          2026-09-29 creator/mail-close: 判断を needsIdentity の引数に移した（mailWarmup.test.ts で確かめる。運営が mail だけを開けても同じ） */}
      {needsIdentity(warmup, isMoneyNavHidden('get-paid')) && (
        <>
          <span>{t('mail.warmup.identityHint')}</span>
          <span>
            <Link to={PAYOUT_SETUP_PATH} className="btn btn-sm btn-ghost">
              {t('mail.warmup.identityAction')}
            </Link>
          </span>
        </>
      )}
    </div>
  );
}
